/**
 * Audio Recorder Hook for capturing voice input
 */
import { useRef, useState, useCallback } from 'react';

export const useAudioRecorder = () => {
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setError(null);
    } catch (err) {
      setError('Failed to access microphone');
      console.error('Microphone error:', err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      return new Promise((resolve) => {
        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/wav',
          });

          // Convert to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Audio = reader.result.split(',')[1];
            resolve(base64Audio);
          };
          reader.readAsDataURL(audioBlob);
        };
      });
    }
  }, [isRecording]);

  const resetRecording = useCallback(() => {
    audioChunksRef.current = [];
    setTranscript('');
  }, []);

  return {
    isRecording,
    transcript,
    setTranscript,
    error,
    setError,
    startRecording,
    stopRecording,
    resetRecording,
  };
};
